import { useContext, useMemo, useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { object } from 'prop-types'
import { useSelector } from 'react-redux'
import {
  CvsAutoComplete,
  CvsTextField,
  CvsFieldMapper,
  InboundObjectValidator,
  CvsFieldMapperV2,
} from '@components'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { connectorContext, adAccountsContext } from '@contexts'
import { api } from 'api'
import { templateCopyContext } from '../context'
import {
  getMappableFields,
  transformIntegrationFieldMappings,
} from '../../../../../components/field-mapperV2/utils'
import { t } from '@lingui/core/macro'

const FullWidth = styled.div`
  width: 100%;
`

const FullWidthTextField = styled(CvsTextField)`
  width: 100%;
`

const facebookFields = [
  'ad_id',
  'ad_name',
  'platform',
  'facebook_positions',
  'instagram_positions',
  'ad_status',
  'ad_created_time',
  'ad_updated_time',
  'creative_id',
  'creative_name',
  'call_to_action_type',
  'creative_object_type',
  'creative_instagram_permalink_url',
  'creative_link_url',
  'creative_object_url',
  'creative_status',
  'creative_template_url',
  'creative_url_tags',
  'creative_video_id',
  'adset_id',
  'adset_name',
  'adset_targeting',
  'adset_bid_amount',
  'campaign_id',
  'campaign_name',
  'campaign_objective',
  'campaign_bid_strategy',
  'campaign_buy_type',
  'campaign_created_time',
  'campaign_start_time',
  'campaign_stop_time',
  'ad_account_id',
  'ad_account_name',
  'carousel_name',
  'carousel_image_hash',
  'generated_link',
]

const FBDefaultMappingFields = [
  {
    cvFieldId: '',
    required: true,
    fieldName: 'ad_id',
    type: 'integration_field',
  },
  {
    cvFieldId: '',
    required: true,
    fieldName: 'creative_id',
    type: 'integration_field',
  },
  {
    cvFieldId: '',
    required: true,
    fieldName: 'generated_link',
    type: 'integration_field',
  },
]

export const FacebookAdsSync = ({ assignment }) => {
  const { template, onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const { byId: adAccounts, fetchAdAccounts } = useContext(adAccountsContext)
  const [loadingFields, setLoadingFields] = useState(true)
  const [fields, setFields] = useState([])

  const fbNewMapping = useSelector(state => state.Auth.features['use-airbyte-for-fb-integration'])

  useEffect(() => {
    if (fbNewMapping) {
      convertFBAdsToV2()
      api
        .get(`/api/v1/integrations/facebook-ads/default-fields`)
        .then(resp => {
          setFields(resp.data)
          setLoadingFields(false)
        })
        .catch(() => {
          setLoadingFields(false)
        })
    }
  }, [])

  useEffect(() => {
    if (assignment.connector_id) {
      fetchAdAccounts(assignment.connector_id)
    }
  }, [assignment.connector_id])

  const mappingValues = useMemo(() => {
    const { metadata } = assignment
    return Object.keys(metadata)
      .filter(opt => opt !== 'adAccountsLinked' && opt !== 'link' && opt !== 'fieldMapping')
      .reduce((acc, key) => ({ ...acc, [key]: metadata[key] }), {})
  }, [template, assignment])

  const fromOptions = useMemo(() => facebookFields.map(f => ({ label: f, value: f })), [])
  const fieldOptions = useMemo(
    () =>
      Object.values(template.templateFieldsById).map(field => ({
        label: field.field?.name,
        value: field.field?.id?.toString(),
      })),

    [template],
  )

  const connectors = useMemo(() => {
    const conns = Object.values(byId)
    return (
      conns.length &&
      conns
        .filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[assignment.connector_id].connector_type_id),
        )
        .map(({ name, id }) => ({
          label: name,
          value: id,
        }))
    )
  }, [assignment, byId])

  const onFacebookAdsSyncChange = useCallback(
    (value, name) => {
      return onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          [name]: value,
        },
      }))
    },
    [(assignment, onChange)],
  )

  const convertFBAdsToV2 = () => {
    onChange(old => ({
      ...old,
      [assignment.id]: {
        ...old[assignment.id],
        mapping_fields: FBDefaultMappingFields,
        new_mapping: true,
      },
    }))
  }

  const onFacebookAdsSyncMetadataChange = useCallback(
    (value, name) =>
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            [name]: value,
          },
        },
      }))[(onChange, assignment)],
  )

  const onFacebookAdsMappingChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          mapping_fields: value,
        },
      }))
    },
    [assignment, onChange],
  )

  const adAccountOptions = (adAccounts[assignment.connector_id] || []).map(acct => ({
    label: acct.name,
    value: acct.account_id,
  }))

  return (
    <FullWidth>
      <FullWidthTextField
        label={t`Integration name`}
        onChange={e => onFacebookAdsSyncChange(e.target.value, 'name')}
        value={assignment.name}
      />
      <CvsAutoComplete
        options={connectors || []}
        onSelected={selected => onFacebookAdsSyncChange(selected, 'connector_id')}
        selected={assignment.connector_id}
        label={t`Facebook account`}
      />
      <CvsAutoComplete
        options={adAccountOptions}
        onSelected={selected => onFacebookAdsSyncMetadataChange(selected, 'adAccountsLinked')}
        selected={formatSelectedOptions(
          true,
          Array.isArray(assignment.metadata.adAccountsLinked)
            ? assignment.metadata.adAccountsLinked
            : (assignment.metadata.adAccountsLinked?.split(',') ?? []),
          adAccountOptions,
        )}
        label={t`Ad accounts`}
        showSpinner={!adAccounts[assignment.connector_id]}
        isMulti
      />
      <InboundObjectValidator connector_id={assignment.connector_id} objectOptions={['ad']} />
      <div style={{ paddingTop: 12, paddingBottom: 12 }}>
        <Typography variant="caption">
          {t`*This integration requires the template to have a link field.`}
        </Typography>
      </div>
      {fbNewMapping ? (
        <div>
          {loadingFields ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
              <CircularProgress color="primary" />
            </div>
          ) : (
            <CvsFieldMapperV2
              mappingType="inbound"
              integrationTitle="Facebook Ads"
              integrationOptions={getMappableFields(fields, [])}
              templateOptions={fieldOptions}
              mappings={transformIntegrationFieldMappings(assignment.mapping_fields)}
              onSelect={value => onFacebookAdsMappingChange(value)}
            />
          )}
        </div>
      ) : (
        <CvsFieldMapper
          fromOptions={fromOptions}
          toOptions={fieldOptions}
          requiredMappings={['ad_id', 'generated_link', 'creative_id']}
          value={mappingValues}
          onSelect={value => {
            const { adAccountsLinked, link } = template.integrationsById[assignment.id].metadata
            const replacement = { adAccountsLinked, link }
            const mappings = Object.keys(value).reduce(
              (acc, key) => ({ ...acc, [key]: value[key] }),
              {},
            )
            onChange(old => ({
              ...old,
              [assignment.id]: {
                ...old[assignment.id],
                metadata: {
                  ...replacement,
                  ...mappings,
                  fieldMapping: Object.keys(mappings).map(key => ({
                    fbParameterName: key,
                    claravineTemplateFieldId: mappings[key],
                  })),
                },
              },
            }))
          }}
        />
      )}
    </FullWidth>
  )
}

FacebookAdsSync.propTypes = {
  assignment: object.isRequired,
}
